import type { CSSProperties, ReactNode } from 'react';
import { MOTS_DES_DESSINS as M, type SceneDuGeste } from './aideDuTelephone';
import {
  A_TOI,
  AGENDA,
  AJOUTER_UNE_TACHE,
  attendQuoi,
  AUTRE_CHOSE,
  C_EST_FAIT,
  C_EST_MA_JOURNEE,
  CREER,
  DEPLACER,
  ENSUITE,
  FAITE,
  iciTuPeuxFaire,
  IMPOSE,
  J_ATTENDS_UN_RETOUR,
  JE_CHOISIS,
  JOINDRE,
  JOURNEE,
  LA_TACHE,
  MAINTENANT,
  motPossibles,
  MOTS_AGENDA_AU_POUCE,
  MOTS_COURAGE,
  MOTS_DE_L_HEURE,
  MOTS_DU_PLUS,
  MOTS_TEMPS_DE_LA_QUESTION,
  NOUVEAU_PROJET,
  ON_ATTEND,
  PAS_AUJOURDHUI,
  PLUS_TARD,
  PLUS_TARD_AUJOURDHUI,
  PROJETS,
  PROPOSE_MOI,
  QUAND_ET_CONDITIONS,
  REGLAGE_TEMPS,
  REGLAGES,
  reponsesDuMonde,
  SUPPRIMER,
  surN,
  TOUS_LES_PROJETS,
  voirLesPossibles,
} from '../partage/vocabulaire';
import { teinteOuAccent } from '../apparence';
import './DessinDuGeste.css';

/**
 * Le petit dessin animé d'un geste de l'Aide du téléphone (ticket 14 de telephone, d'après la
 * maquette validée le 2026-09-28 ; revu au ticket 08 du chantier `aide`, puis au ticket 05 du
 * chantier `telephone-cadre` : la barre des cinq boutons, Maintenant en grand, le bouton
 * Réglages, et la liste d'un projet à la place de la Carte ; puis au ticket 07 du chantier
 * `telephone-journee-agenda` : la question, « Propose-moi », « Je choisis », la journée en cours
 * et « Plus tard », « Changer ma journée », la semaine en cartes et le mois en barres ; puis au
 * ticket 06 du chantier `telephone-projets-fiche` : les tuiles de Projets, un projet et « Ici, tu
 * peux faire », le fil sur une liste, la fiche en pastilles, son panneau Réglages, le + qui
 * ajoute là où l'on est, le nouveau projet et ses couleurs) : des lignes, des puces, et le doigt
 * qui touche (un rond qui se pose) ou qui reste posé (un anneau qui se remplit). Un décor : rien
 * ne s'y touche, les lecteurs d'écran le sautent. L'animation s'arrête si le téléphone demande
 * moins de mouvement (`DessinDuGeste.css`). Il ne montre que ce que l'écran montre : ni champ en
 * bas d'un projet, ni bulles de la Carte, ni flèches dans la journée, ni rond devant une étape.
 */
export function DessinDuGeste({ scene }: { scene: SceneDuGeste }) {
  return (
    <div className={`geste-tel geste-tel--${scene}`} aria-hidden="true">
      {DESSINS[scene]()}
    </div>
  );
}

/** La teinte d'un projet (`--dossier-1` à `--dossier-5`), pour sa tuile ou sa pastille. */
function teinte(couleur: string): CSSProperties {
  return { ['--teinte' as string]: teinteOuAccent(couleur) };
}

/** Le cercle d'un projet, « X sur Y » à côté : il se remplit, jamais de pourcentage. */
function Cercle({ fait, total, surTuile = false }: { fait: number; total: number; surTuile?: boolean }) {
  return (
    <span className="geste-tel-sur">
      <i className={`geste-tel-cercle${surTuile ? ' geste-tel-cercle--tuile' : ''}`} style={{ ['--part' as string]: `${Math.round((fait / total) * 360)}deg` }} />
      {surN(fait, total)}
    </span>
  );
}

/** Le doigt : il touche (un rond qui se pose et repart), ou reste posé. */
function Doigt({ x, y, long = false }: { x: number; y: number; long?: boolean }) {
  return <div className={`geste-tel-doigt${long ? '' : ' geste-tel-doigt--touche'}`} style={{ left: `${x}%`, top: y }} />;
}

/**
 * Une ligne de l'app, posée en haut (`top`) ou en bas (`bottom`) du dessin. `trait` : un bouton
 * cerné ; `monde` : un rendez-vous, sur l'ambre ; `tenu` : un lien discret ; `apparait` : ce qui
 * paraît après le toucher.
 */
function Ligne({
  top,
  bottom,
  sorte,
  children,
}: {
  top?: number;
  bottom?: number;
  sorte?: 'action' | 'libere' | 'trait' | 'monde' | 'tenu' | 'apparait';
  children: ReactNode;
}) {
  return (
    <div className={`geste-tel-ligne${sorte ? ` geste-tel-ligne--${sorte}` : ''}`} style={{ top, bottom }}>
      {children}
    </div>
  );
}

/** Une ligne de la liste d'un projet : une tâche a son rond devant, une étape son › au bout. */
function LigneDeListe({ top, etape = false, children }: { top: number; etape?: boolean; children: ReactNode }) {
  return (
    <Ligne top={top}>
      {!etape && <span className="geste-tel-rond" />}
      {children}
      {etape && <span className="geste-tel-a-droite geste-tel-entrer">›</span>}
    </Ligne>
  );
}

/** Une tâche de la journée : son heure, son titre, ses gestes au bout (×, ↻, « Faite »). */
function Creneau({ top, heure, gestes, sorte, children }: { top: number; heure: string; gestes?: string; sorte?: 'libere'; children: ReactNode }) {
  return (
    <Ligne top={top} sorte={sorte}>
      <b className="geste-tel-heure">{heure}</b>
      {children}
      {gestes && <span className="geste-tel-gestes">{gestes}</span>}
    </Ligne>
  );
}

/** Une rangée de puces (des boutons de la question, de « Je choisis », de l'Agenda). */
function Rangee({ top, children }: { top: number; children: ReactNode }) {
  return (
    <div className="geste-tel-rangee" style={{ top }}>
      {children}
    </div>
  );
}

/** Une puce : choisie (`on`), sur l'ambre de ce qu'on attend (`monde`), ou en pointillés (`vide`). */
function Puce({ on = false, sorte, children }: { on?: boolean; sorte?: 'monde' | 'vide'; children: ReactNode }) {
  return <span className={`geste-tel-puce${on ? ' geste-tel-puce--on' : ''}${sorte ? ` geste-tel-puce--${sorte}` : ''}`}>{children}</span>;
}

/** Les noms des trois groupes du panneau Réglages de la fiche, dans leur ordre. */
const GROUPES_DES_REGLAGES = { tache: LA_TACHE, quand: QUAND_ET_CONDITIONS, joindre: JOINDRE } as const;

const A = MOTS_AGENDA_AU_POUCE;

/** La barre du bas : les cinq boutons, le + au milieu. */
function BarreDuBas({ allumee }: { allumee: boolean }) {
  return (
    <div className="geste-tel-barre">
      <span className={allumee ? 'geste-tel-barre-on' : undefined}>{MAINTENANT}</span>
      <span>{PROJETS}</span>
      <span className="geste-tel-plus">+</span>
      <span>{AGENDA}</span>
      <span>{JOURNEE}</span>
    </div>
  );
}

const DESSINS: Record<SceneDuGeste, () => ReactNode> = {
  barre: () => (
    <>
      <BarreDuBas allumee />
      <Doigt x={10} y={227} />
    </>
  ),
  maintenant: () => (
    <>
      <div className="geste-tel-une">
        <span className="geste-tel-une-raison">{M.raisonDeLaUne}</span>
        <b className="geste-tel-une-titre">{M.preavis}</b>
        <span className="geste-tel-une-projet">{M.demenager}</span>
        <span className="geste-tel-une-gestes">
          <span className="geste-tel-une-fait">{C_EST_FAIT}</span>
          <span className="geste-tel-une-autre">{AUTRE_CHOSE}</span>
        </span>
      </div>
      <Ligne bottom={14}>
        <span className="geste-tel-tenu">{ENSUITE}</span>
        {M.cartons}
      </Ligne>
      <Doigt x={72} y={117} />
    </>
  ),
  reglages: () => (
    <>
      <Ligne top={14}>
        <span>☰</span>
        <b>{REGLAGES}</b>
        <span className="geste-tel-tenu">{M.resumeDesReglages}</span>
      </Ligne>
      <div className="geste-tel-feuille">
        <span className="geste-tel-tenu">{REGLAGE_TEMPS}</span>
        <span className="geste-tel-puces">
          {M.durees.map((d, i) => (
            <span key={d} className={`geste-tel-puce${i === 0 ? ' geste-tel-puce--on' : ''}`}>
              {d}
            </span>
          ))}
        </span>
        <span className="geste-tel-voir">{voirLesPossibles(M.possibles)}</span>
      </div>
      <Doigt x={22} y={31} />
    </>
  ),
  projets: () => (
    <>
      {M.tuiles.map((t, i) => (
        <div key={t.nom} className="geste-tel-tuile" style={{ ...teinte(t.couleur), top: 10 + i * 120 }}>
          <span className="geste-tel-tuile-tete">
            <b>{t.nom}</b>
            <span className="geste-tel-tuile-nombre">
              <b>{t.possibles}</b> {motPossibles(t.possibles)}
            </span>
          </span>
          {t.possibles > 0 ? (
            <span className="geste-tel-tuile-encart">
              <small>{A_TOI}</small>
              {M.preavis}
            </span>
          ) : (
            <span className="geste-tel-tuile-encart geste-tel-tuile-encart--monde">
              <small>{ON_ATTEND}</small>
              {reponsesDuMonde(1)}
            </span>
          )}
          <Cercle fait={t.fait} total={t.total} surTuile />
        </div>
      ))}
      <Doigt x={50} y={62} />
    </>
  ),
  toucher: () => (
    <>
      <Rangee top={12}>
        <b>{M.societe}</b>
        <span className="geste-tel-a-droite">
          <Cercle fait={3} total={9} />
        </span>
      </Rangee>
      <Ligne top={42} sorte="tenu">
        {iciTuPeuxFaire(M.ici)} ▸
      </Ligne>
      <LigneDeListe top={84} etape>{M.statut}</LigneDeListe>
      <LigneDeListe top={128} etape>{M.banque}</LigneDeListe>
      <LigneDeListe top={172}>{M.rassembler}</LigneDeListe>
      <Doigt x={50} y={145} />
    </>
  ),
  fil: () => (
    <>
      <div className="geste-tel-fil">
        <b>{M.societe}</b> › {M.banque}
      </div>
      <div className="geste-tel-titre geste-tel-titre--sous-le-fil">
        {M.banque} <span className="geste-tel-tenu">☰</span>
      </div>
      <LigneDeListe top={80}>{M.prendreRendezVous}</LigneDeListe>
      <LigneDeListe top={124}>{M.deposerCapital}</LigneDeListe>
      <Doigt x={19} y={20} />
    </>
  ),
  long: () => (
    <>
      <div className="geste-tel-titre">
        {M.banque} <span className="geste-tel-tenu">☰</span>
      </div>
      <LigneDeListe top={48}>{M.rassembler}</LigneDeListe>
      <LigneDeListe top={94} etape>{M.rendezVousBanque}</LigneDeListe>
      <svg className="geste-tel-anneau" style={{ left: '40%', top: 65 }} viewBox="0 0 58 58">
        <circle cx="29" cy="29" r="26" />
      </svg>
      <Doigt x={40} y={65} long />
      <div className="geste-tel-fiche">
        <b>{M.rassembler}</b>
        <br />
        <span className="geste-tel-tenu">☰ {REGLAGES}</span>
        <br />
        <span className="geste-tel-tenu">{M.ficheNotes}</span>
        <br />
        {C_EST_FAIT}
      </div>
    </>
  ),
  fiche: () => (
    <>
      <div className="geste-tel-titre">{M.rendezVousBanque}</div>
      <Rangee top={40}>
        <Puce sorte="monde">⏸ {attendQuoi(M.attendu, 0)}</Puce>
        <span className="geste-tel-a-droite">
          <Puce>☰ {REGLAGES}</Puce>
        </span>
      </Rangee>
      <Rangee top={76}>
        {M.dates.map((d) => (
          <Puce key={d} sorte="monde">
            {d}
          </Puce>
        ))}
      </Rangee>
      <Rangee top={112}>
        {M.infos.map((d) => (
          <Puce key={d}>{d}</Puce>
        ))}
      </Rangee>
      <Ligne top={150}>
        <span className="geste-tel-tenu">{M.ficheNotes}</span>
      </Ligne>
      <Rangee top={204}>
        <span className="geste-tel-choix geste-tel-choix--fort">{C_EST_FAIT}</span>
        <span className="geste-tel-choix">{J_ATTENDS_UN_RETOUR}</span>
      </Rangee>
      <Doigt x={56} y={124} />
    </>
  ),
  panneau: () => (
    <>
      <div className="geste-tel-groupes">
        {M.reglagesDeLaFiche.map((g) => (
          <div key={g.groupe} className={`geste-tel-groupe geste-tel-groupe--${g.groupe}`}>
            <small>{GROUPES_DES_REGLAGES[g.groupe]}</small>
            <span className="geste-tel-puces">
              {g.regle.map((r) => (
                <Puce key={r} on>
                  {r} ×
                </Puce>
              ))}
              {g.vide.map((v) => (
                <Puce key={v} sorte="vide">
                  + {v}
                </Puce>
              ))}
            </span>
          </div>
        ))}
      </div>
      <Rangee top={208}>
        <span className="geste-tel-choix">{DEPLACER} ›</span>
        <span className="geste-tel-a-droite geste-tel-supprimer">{SUPPRIMER}</span>
      </Rangee>
      <Doigt x={64} y={112} />
    </>
  ),
  ajouter: () => (
    <>
      <Ligne top={12}>
        <b>{MOTS_DU_PLUS.ajouterDans(M.banque)}</b>
      </Ligne>
      <Rangee top={58}>
        <Puce on>{MOTS_DU_PLUS.sortes.tache}</Puce>
        <Puce>{MOTS_DU_PLUS.sortes.idee}</Puce>
      </Rangee>
      <Ligne top={94}>
        <span className="geste-tel-tenu">{M.aAjouter}</span>
      </Ligne>
      <Ligne top={140} sorte="action">{MOTS_DU_PLUS.ajouter}</Ligne>
      <BarreDuBas allumee={false} />
      <Doigt x={50} y={227} />
    </>
  ),
  nouveau: () => (
    <>
      <Ligne top={10}>
        <b>{NOUVEAU_PROJET}</b>
      </Ligne>
      <Ligne top={50}>{M.demenager}</Ligne>
      <Rangee top={96}>
        <span className="geste-tel-tenu">{MOTS_DU_PLUS.saCouleur}</span>
        {Object.keys(MOTS_DU_PLUS.couleurs).map((c, i) => (
          <i key={c} className={`geste-tel-couleur${i === 0 ? ' geste-tel-couleur--choisie' : ''}`} style={teinte(c)} />
        ))}
      </Rangee>
      <Ligne top={128} sorte="action">{CREER}</Ligne>
      <Ligne top={170} sorte="tenu">{MOTS_DU_PLUS.partirDUnModele}</Ligne>
      <Ligne top={206} sorte="tenu">{MOTS_DU_PLUS.importerUnPlan}</Ligne>
      <Doigt x={66} y={145} />
    </>
  ),
  coche: () => (
    <>
      <Ligne top={40}>
        <span className="geste-tel-rond geste-tel-rond--fait" />
        {M.rassembler}
      </Ligne>
      <Ligne top={90}>
        <span className="geste-tel-rond" />
        {M.preavis}
      </Ligne>
      <Ligne top={150} sorte="libere">{M.libere}</Ligne>
      <Doigt x={9} y={54} />
    </>
  ),
  question: () => (
    <>
      <Rangee top={16}>
        {MOTS_TEMPS_DE_LA_QUESTION.map(([v, mot]) => (
          <Puce key={v} on={mot === M.temps}>
            {mot}
          </Puce>
        ))}
      </Rangee>
      <Rangee top={58}>
        {Object.entries(MOTS_COURAGE).map(([c, mot]) => (
          <Puce key={c} on={c === 'normal'}>
            {mot}
          </Puce>
        ))}
      </Rangee>
      <Ligne bottom={62} sorte="action">{PROPOSE_MOI}</Ligne>
      <Ligne bottom={14} sorte="trait">{JE_CHOISIS}</Ligne>
      <Doigt x={50} y={171} />
    </>
  ),
  propose: () => (
    <>
      <Creneau top={14} heure={M.heures[0]} gestes="↻ ×">{M.rassembler}</Creneau>
      <Ligne top={58} sorte="monde">
        <b className="geste-tel-heure">{M.heures[1]}</b>
        {M.rendezVous}
        <span className="geste-tel-gestes">{IMPOSE}</span>
      </Ligne>
      <Creneau top={102} heure={M.heures[2]} gestes="↻ ×">{M.preavis}</Creneau>
      <Ligne top={146} sorte="tenu">{AJOUTER_UNE_TACHE}</Ligne>
      <Ligne bottom={14} sorte="action">{C_EST_MA_JOURNEE}</Ligne>
      <Doigt x={91} y={31} />
    </>
  ),
  choisis: () => (
    <>
      <Rangee top={14}>
        <Puce>{M.projetChoisi} ▾</Puce>
        <Puce>{M.dureeChoisie} ▾</Puce>
        <Puce>{MOTS_COURAGE.normal} ▾</Puce>
      </Rangee>
      <Creneau top={52} heure={M.heures[0]} gestes="×" sorte="libere">
        {M.cartons}
      </Creneau>
      <Ligne top={98}>
        {M.preavis}
        <span className="geste-tel-bouton">+</span>
      </Ligne>
      <Ligne top={144}>
        {M.aAjouter}
        <span className="geste-tel-bouton">+</span>
      </Ligne>
      <Ligne bottom={14} sorte="action">{C_EST_MA_JOURNEE}</Ligne>
      <Doigt x={89} y={115} />
    </>
  ),
  journee: () => (
    <>
      <div className="geste-tel-une">
        <span className="geste-tel-une-raison">
          {MOTS_DE_L_HEURE.en_ce_moment} · {M.heures[1]}
        </span>
        <b className="geste-tel-une-titre">{M.preavis}</b>
        <span className="geste-tel-une-projet">{M.demenager}</span>
        <span className="geste-tel-une-gestes">
          <span className="geste-tel-une-fait">{C_EST_FAIT}</span>
          <span className="geste-tel-une-autre">{PLUS_TARD}</span>
        </span>
      </div>
      <div className="geste-tel-feuille">
        <span className="geste-tel-choix">{PLUS_TARD_AUJOURDHUI}</span>
        <span className="geste-tel-choix">{PAS_AUJOURDHUI}</span>
      </div>
      <Doigt x={72} y={117} />
    </>
  ),
  changer: () => (
    <>
      <Creneau top={14} heure={M.heures[0]} gestes={FAITE}>
        <span className="geste-tel-tenu">{M.rassembler}</span>
      </Creneau>
      <Creneau top={58} heure={M.heures[1]} gestes="×">{M.preavis}</Creneau>
      <Creneau top={102} heure={M.heures[2]} gestes="×">{M.cartons}</Creneau>
      <Ligne top={146} sorte="tenu">{AJOUTER_UNE_TACHE}</Ligne>
      <Ligne bottom={14} sorte="action">{C_EST_MA_JOURNEE}</Ligne>
      <Doigt x={93} y={75} />
    </>
  ),
  agenda: () => (
    <>
      <Rangee top={12}>
        <Puce on>{A.semaine}</Puce>
        <Puce>{A.mois}</Puce>
        <span className="geste-tel-a-droite">
          <Puce>{TOUS_LES_PROJETS} ▾</Puce>
        </span>
      </Rangee>
      {M.agenda.map((a, i) => (
        <Ligne key={a.jour} top={52 + i * 44} sorte={a.sorte === 'journee' ? 'libere' : undefined}>
          <b className="geste-tel-heure">{a.jour}</b>
          <span className={a.sorte === 'monde' ? 'geste-tel-signe--monde' : undefined}>{a.signe}</span>
          {a.quoi ?? A.tachesDeLaJournee(2, M.heures[0])}
        </Ligne>
      ))}
      <Ligne top={186} sorte="apparait">
        <span className="geste-tel-choix">{A.relancer}</span>
        <span className="geste-tel-choix">{A.arrive}</span>
      </Ligne>
      <Doigt x={50} y={157} />
    </>
  ),
  mois: () => (
    <>
      <Rangee top={12}>
        <Puce>{A.semaine}</Puce>
        <Puce on>{A.mois}</Puce>
      </Rangee>
      <div className="geste-tel-mois">
        {M.mois.map((c) => (
          <span key={c.n} className={`geste-tel-case${c.n === 1 ? ' geste-tel-case--touche' : ''}`}>
            {c.n}
            <span className="geste-tel-barres">
              {c.barres.map((b, i) => (
                <i key={i} className={`geste-tel-trait geste-tel-trait--${b}`} />
              ))}
            </span>
          </span>
        ))}
      </div>
      <Ligne bottom={56} sorte="apparait">
        <b>{M.jourTouche}</b>
        <span className="geste-tel-gestes">{A.ouvrirLeJour} ›</span>
      </Ligne>
      <Ligne bottom={14} sorte="apparait">
        <span className="geste-tel-signe--monde">{M.agenda[2].signe}</span>
        {M.agenda[2].quoi}
      </Ligne>
      <Doigt x={50} y={68} />
    </>
  ),
};
